import React, { useEffect, useCallback } from "react";
import {
  LightboxOverlay,
  LightboxContent,
  LightboxClose,
  LightboxImage,
  LightboxVideo,
  LightboxTitle,
  LightboxDescription,
  LightboxNav,
  LightboxNavButton,
  LightboxCounter,
} from "./Lightbox.style";

const Lightbox = ({
  isOpen,
  onClose,
  items,
  currentIndex,
  onNavigate,
  title,
  description,
}) => {
  const currentItem = items[currentIndex];
  const isVideo = currentItem?.type === "video" || currentItem?.src?.endsWith(".mp4");

  const handlePrev = useCallback(() => {
    const newIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
    onNavigate(newIndex);
  }, [currentIndex, items.length, onNavigate]);

  const handleNext = useCallback(() => {
    const newIndex = currentIndex === items.length - 1 ? 0 : currentIndex + 1;
    onNavigate(newIndex);
  }, [currentIndex, items.length, onNavigate]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, handlePrev, handleNext]);

  if (!isOpen || !currentItem) return null;

  return (
    <LightboxOverlay onClick={onClose}>
      <LightboxContent onClick={(e) => e.stopPropagation()}>
        <LightboxClose onClick={onClose} aria-label="Close">
          ×
        </LightboxClose>

        {items.length > 1 && (
          <LightboxNav>
            <LightboxNavButton onClick={handlePrev} aria-label="Previous">
              ‹
            </LightboxNavButton>
            <LightboxCounter>
              {currentIndex + 1} / {items.length}
            </LightboxCounter>
            <LightboxNavButton onClick={handleNext} aria-label="Next">
              ›
            </LightboxNavButton>
          </LightboxNav>
        )}

        {isVideo ? (
          <LightboxVideo controls autoPlay>
            <source src={currentItem.src} type="video/mp4" />
            Your browser does not support the video tag.
          </LightboxVideo>
        ) : (
          <LightboxImage src={currentItem.src} alt={currentItem.title || title} />
        )}

        {(title || currentItem.title) && (
          <LightboxTitle>{currentItem.title || title}</LightboxTitle>
        )}
        {(description || currentItem.description) && (
          <LightboxDescription>
            {currentItem.description || description}
          </LightboxDescription>
        )}
      </LightboxContent>
    </LightboxOverlay>
  );
};

export default Lightbox;
